import React from 'react';
import { Info, Droplet, Ruler, Minus, Gauge, BookOpen, Check, Thermometer, Calculator, BarChart, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о расчетах расхода воды в трубах
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Расчет расхода воды в трубах: формулы, стандарты, рекомендации
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-500" />
          Основные формулы для расчета
        </h3>
        <p>
          Расчет гидравлических параметров трубопроводов базируется на фундаментальных уравнениях гидродинамики, 
          адаптированных для практического применения. Для определения взаимосвязей между расходом, давлением, 
          диаметром трубы и другими параметрами используются следующие формулы:
        </p>

        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600 my-4">
          <h4 className="text-lg font-medium mb-2">Формула Дарси-Вейсбаха</h4>
          <p className="mb-2">Основная формула для расчета потерь давления на трение при движении жидкости в трубе:</p>
          <div className="text-center font-mono text-lg mb-2">
            Δp = λ · (L/D) · (ρv²/2)
          </div>
          <p className="text-sm">
            где:<br />
            Δp - потери давления (Па)<br />
            λ - коэффициент гидравлического трения<br />
            L - длина трубы (м)<br />
            D - внутренний диаметр трубы (м)<br />
            ρ - плотность жидкости (кг/м³)<br />
            v - скорость потока (м/с)
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600 my-4">
          <h4 className="text-lg font-medium mb-2">Формула расчета скорости потока</h4>
          <p className="mb-2">Связь между объемным расходом и скоростью потока:</p>
          <div className="text-center font-mono text-lg mb-2">
            v = Q / (π · D² / 4)
          </div>
          <p className="text-sm">
            где:<br />
            v - скорость потока (м/с)<br />
            Q - объемный расход (м³/с)<br />
            D - внутренний диаметр трубы (м)
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600 my-4">
          <h4 className="text-lg font-medium mb-2">Формула для определения коэффициента трения (λ)</h4>
          <p className="mb-2">Для практических расчетов используется формула Альтшуля, работающая для всех режимов течения:</p>
          <div className="text-center font-mono text-lg mb-2">
            λ = 0.11 · (Δ/D + 68/Re)^0.25
          </div>
          <p className="text-sm">
            где:<br />
            λ - коэффициент гидравлического трения<br />
            Δ - абсолютная шероховатость стенок трубы (м)<br />
            D - внутренний диаметр трубы (м)<br />
            Re - число Рейнольдса
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600 my-4">
          <h4 className="text-lg font-medium mb-2">Формула для расчета числа Рейнольдса</h4>
          <p className="mb-2">Параметр, определяющий режим течения жидкости:</p>
          <div className="text-center font-mono text-lg mb-2">
            Re = (v · D) / ν
          </div>
          <p className="text-sm">
            где:<br />
            Re - число Рейнольдса<br />
            v - скорость потока (м/с)<br />
            D - внутренний диаметр трубы (м)<br />
            ν - кинематическая вязкость жидкости (м²/с)
          </p>
        </div>

        <h3 className="flex items-center gap-2">
        <Minus className="w-5 h-5 text-blue-500" />
          Типы труб и их характеристики
        </h3>
        <p>
          В современных системах водоснабжения в России используются различные типы трубопроводов, каждый со своими 
          преимуществами, недостатками и областями применения. При выборе материала труб необходимо учитывать не только 
          гидравлические характеристики, но и требования к долговечности, безопасности и соответствию нормативам.
        </p>

        <div className="overflow-x-auto my-4">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-blue-50 dark:bg-blue-900/30">
                <th className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-left">Материал трубы</th>
                <th className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-left">Шероховатость, мм</th>
                <th className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-left">Диапазон диаметров, мм</th>
                <th className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-left">Срок службы, лет</th>
                <th className="border border-gray-300 dark:border-gray-700 px-3 py-2 text-left">Рекомендуемое применение</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Стальные оцинкованные</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">0.15-0.3</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">15-150</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">15-25</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Холодное водоснабжение, пожаротушение</td>
              </tr>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Полипропиленовые (ПП)</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">0.004-0.01</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">16-110</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">50+</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Холодное и горячее водоснабжение</td>
              </tr>
              <tr>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Медные</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">0.0015-0.01</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">10-108</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">50+</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Питьевое водоснабжение, отопление</td>
              </tr>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">ПВХ (поливинилхлорид)</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">0.007-0.02</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">16-315</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">30-40</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Наружные сети, канализация</td>
              </tr>
              <tr>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">ПЭ (полиэтилен)</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">0.007-0.015</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">20-630</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">50+</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Наружные сети, газоснабжение</td>
              </tr>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Чугунные</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">0.25-1.0</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">50-300</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">80-100</td>
                <td className="border border-gray-300 dark:border-gray-700 px-3 py-2">Магистральные сети, канализация</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-blue-500" />
          Стандартные диаметры труб в России
        </h3>
        <p>
          В России номинальные диаметры труб определяются согласно ГОСТ и нормативной документации. Самые распространенные 
          размеры труб для внутренних систем водоснабжения:
        </p>
        <ul>
          <li>
            <strong>Стальные трубы (ГОСТ 3262-75):</strong> Ду15, Ду20, Ду25, Ду32, Ду40, Ду50 мм. В миллиметрах: 
            15, 20, 25, 32, 40, 50 мм (внутренний диаметр).
          </li>
          <li>
            <strong>Полипропиленовые трубы (ГОСТ 32415-2013):</strong> 16, 20, 25, 32, 40, 50, 63, 75, 90, 110 мм (наружный диаметр).
          </li>
          <li>
            <strong>Медные трубы (ГОСТ Р 52318-2005):</strong> 10, 12, 15, 18, 22, 28, 35, 42, 54, 76, 108 мм (наружный диаметр).
          </li>
          <li>
            <strong>ПВХ трубы (ГОСТ 32415-2013):</strong> 16, 20, 25, 32, 40, 50, 63, 75, 90, 110, 125, 140, 160, 200 мм (наружный диаметр).
          </li>
        </ul>
        <p>
          Внутренний диаметр зависит от толщины стенки трубы, которая определяется рабочим давлением системы.
          Для полимерных труб (ПП, ПВХ, ПЭ) обычно указывается внешний диаметр и класс давления (PN), определяющий толщину стенки. 
        </p>

        <h3 className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-blue-500" />
          Нормативные требования к скорости потока и потерям давления
        </h3>
        <p>
          Согласно российским нормативным документам (СП 30.13330.2020, СП 31.13330.2021), существуют рекомендации по допустимым 
          скоростям потока и потерям давления в трубопроводах. Эти параметры важны для правильного проектирования систем и 
          обеспечения их надежной работы.
        </p>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Рекомендуемые скорости потока:</h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm space-y-1 list-disc pl-5 mt-2">
            <li>Для внутренних систем хозяйственно-питьевого водопровода: 0.5-1.5 м/с</li>
            <li>Для внутренних систем противопожарного водопровода: до 3.0 м/с</li>
            <li>Для наружных водопроводных сетей: 0.7-2.5 м/с</li>
            <li>Для водопроводных вводов в здания: до 2.0 м/с</li>
            <li>Для циркуляционных систем горячего водоснабжения: 0.3-1.0 м/с</li>
          </ul>
        </div>
        
        <p>
          Для подбора диаметра трубопровода используется также удельная потеря давления на единицу длины. 
          Согласно нормативам, в жилых зданиях рекомендуется принимать удельные потери:
        </p>
        <ul>
          <li>Для холодного водоснабжения: 100-300 Па/м (0.001-0.003 бар/м)</li>
          <li>Для горячего водоснабжения: 65-300 Па/м (0.00065-0.003 бар/м)</li>
          <li>Для циркуляционных трубопроводов: 30-100 Па/м (0.0003-0.001 бар/м)</li>
        </ul>
        
        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-800 my-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-orange-700 dark:text-orange-300 font-medium">Важно!</h4>
              <p className="text-orange-600 dark:text-orange-400 text-sm mt-1">
                При проектировании трубопроводов следует учитывать, что со временем внутренняя поверхность труб подвергается 
                коррозии и зарастанию (особенно стальные трубы), что приводит к увеличению гидравлического сопротивления. 
                Рекомендуется закладывать запас 15-20% при расчетах для компенсации этого эффекта.
              </p>
            </div>
          </div>
        </div>

        <h3 className="flex items-center gap-2">
          <Thermometer className="w-5 h-5 text-blue-500" />
          Влияние температуры на расчеты
        </h3>
        <p>
          Температура воды существенно влияет на её физические свойства, в частности на вязкость и плотность, 
          что напрямую сказывается на гидравлических расчетах. С ростом температуры вязкость воды снижается, 
          что приводит к уменьшению гидравлического сопротивления.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
            <h4 className="font-medium mb-2">Вязкость воды при разных температурах:</h4>
            <ul className="space-y-1 text-sm">
              <li>При 5°C: 1.519×10<sup>-6</sup> м²/с</li>
              <li>При 10°C: 1.307×10<sup>-6</sup> м²/с</li>
              <li>При 20°C: 1.004×10<sup>-6</sup> м²/с</li>
              <li>При 40°C: 0.658×10<sup>-6</sup> м²/с</li>
              <li>При 60°C: 0.475×10<sup>-6</sup> м²/с</li>
              <li>При 80°C: 0.365×10<sup>-6</sup> м²/с</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
            <h4 className="font-medium mb-2">Плотность воды при разных температурах:</h4>
            <ul className="space-y-1 text-sm">
              <li>При 5°C: 999.97 кг/м³</li>
              <li>При 10°C: 999.70 кг/м³</li>
              <li>При 20°C: 998.21 кг/м³</li>
              <li>При 40°C: 992.22 кг/м³</li>
              <li>При 60°C: 983.20 кг/м³</li>
              <li>При 80°C: 971.82 кг/м³</li>
            </ul>
          </div>
        </div>
        
        <p>
          При расчете трубопроводов горячего водоснабжения (температура 60-75°C) следует учитывать, что реальные 
          потери давления будут на 10-15% ниже, чем для холодной воды при том же расходе и диаметре трубы.
        </p>

        <h3 className="flex items-center gap-2">
          <BarChart className="w-5 h-5 text-blue-500" />
          Режимы течения и их влияние на расчет
        </h3>
        <p>
          Режим течения жидкости в трубе (ламинарный, переходный или турбулентный) оказывает значительное влияние на 
          гидравлические потери и определяется числом Рейнольдса (Re):
        </p>
        <ul>
          <li><strong>Ламинарный режим</strong> (Re &lt; 2300): Частицы движутся параллельными слоями без перемешивания.</li>
          <li><strong>Переходный режим</strong> (2300 &lt; Re &lt; 4000): Нестабильное течение с чередованием ламинарных и турбулентных участков.</li>
          <li><strong>Турбулентный режим</strong> (Re &gt; 4000): Хаотичное движение частиц с интенсивным перемешиванием.</li>
        </ul>
        
        <p>
          В системах водоснабжения большинство потоков являются турбулентными. Для таких потоков характерна квадратичная 
          зависимость гидравлических потерь от скорости, в то время как для ламинарных потоков зависимость линейная.
        </p>
        
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
            <Check className="w-4 h-4" />
            Практические рекомендации для проектирования
          </h4>
          <ul className="text-green-600 dark:text-green-400 text-sm space-y-2 list-disc pl-5 mt-2">
            <li>
              <strong>Для магистральных трубопроводов:</strong> Оптимальная скорость 1-1.5 м/с. 
              При такой скорости достигается баланс между стоимостью труб и энергозатратами на преодоление сопротивления.
            </li>
            <li>
              <strong>Для стояков:</strong> Скорость до 1.5 м/с. Более высокие скорости могут вызывать шум.
            </li>
            <li>
              <strong>Для подводок к приборам:</strong> Скорость до 2.5 м/с. Важно ограничивать потери давления для 
              обеспечения нормативного напора у приборов.
            </li>
            <li>
              <strong>Для систем рециркуляции ГВС:</strong> Скорость 0.3-0.5 м/с. Низкие скорости выбираются для снижения 
              энергозатрат на циркуляцию.
            </li>
            <li>
              <strong>Для противопожарных систем:</strong> Допускаются высокие скорости (до 3 м/с и более) ввиду 
              временного характера работы таких систем.
            </li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-500" />
          Трубопроводные системы в российских условиях
        </h3>
        <p>
          При проектировании систем водоснабжения в России необходимо учитывать ряд специфических факторов, 
          которые могут отличаться от зарубежных норм и практик:
        </p>
        
        <ul className="space-y-2 my-4">
          <li>
            <strong>Климатические условия:</strong> В регионах с холодным климатом водопроводы должны прокладываться с учетом 
            глубины промерзания грунта и требуют теплоизоляции для предотвращения замерзания.
          </li>
          <li>
            <strong>Качество воды:</strong> В ряде регионов России водопроводная вода содержит высокое количество примесей и солей, 
            что может привести к образованию отложений и коррозии труб. Это требует использования материалов, устойчивых к агрессивной среде.
          </li>
          <li>
            <strong>Давление в системах:</strong> Давление в централизованных системах водоснабжения может варьироваться от 2 до 9 бар, 
            что необходимо учитывать при выборе материала и класса давления труб (PN).
          </li>
          <li>
            <strong>Высотность зданий:</strong> В многоэтажных зданиях требуется зонирование системы водоснабжения. 
            Согласно СП 30.13330.2020, максимальное гидростатическое давление не должно превышать 0.45 МПа (4.5 бар) 
            на отметке наиболее низко расположенных санитарно-технических приборов.
          </li>
          <li>
            <strong>Нормы расхода:</strong> СП 30.13330.2020 устанавливает нормы водопотребления для различных типов зданий, 
            что влияет на расчетные расходы и требуемые диаметры труб.
          </li>
        </ul>
        
        <p>
          В современных российских проектах все чаще применяются полимерные трубы (ПП, сшитый полиэтилен, металлопластиковые) 
          для внутренних систем водоснабжения. Они обладают меньшей шероховатостью, менее подвержены коррозии и образованию 
          отложений, что обеспечивает стабильные гидравлические характеристики в течение всего срока службы.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Дополнительные материалы по теме:</h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm space-y-1 list-disc pl-5 mt-2">
            <li>СП 30.13330.2020 "Внутренний водопровод и канализация зданий"</li>
            <li>СП 31.13330.2021 "Водоснабжение. Наружные сети и сооружения"</li>
            <li>А.Ф. Шонин, "Водоснабжение и водоотведение жилой застройки"</li>
            <li>В.С. Кедров, "Санитарно-техническое оборудование зданий"</li>
            <li>Ф.А. Шевелев, "Таблицы для гидравлического расчета водопроводных труб"</li>
          </ul>
        </div>
        
        <p className="font-semibold mt-6">
          Данный калькулятор позволяет быстро рассчитать гидравлические параметры трубопроводов согласно актуальным российским нормам,
          что делает его незаменимым инструментом для инженеров-проектировщиков, монтажников и студентов профильных специальностей.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;